import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
} from 'react'
import {
    faChevronDown,
    faChevronUp,
    faPlusCircle,
    faSpinner,
    faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from '../../common/form/inputs/Button'
import Modal from '../../common/modals/Modal'
import TagsInput from '../../common/form/inputs/TagsInput'
import SegmentedToggleInput from '../../common/form/inputs/SegmentedToggleInput'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { formatPersonName } from '../../../utils/personUtils'
import PersonBadge from '../../common/person/PersonBadge'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'
import { toast } from 'sonner'
import {
    JOB_FUNCTION_VALUES,
    SENIORITY_VALUES,
    type ExperienceBounds,
    type ExperienceTimeframe,
    type FilterInputs,
    type JobFunction,
    type KeywordGroup,
    type KeywordLogic,
    type SearchInvocationOptions,
    type Seniority,
    applyHelixSearchFilters,
} from './filterTypes'
import {
    ExcludeCompanyBadge,
    ExcludeJobTitleBadge,
    IncludeCompanyBadge,
    IncludeJobTitleBadge,
    JobFunctionBadge,
    KeywordGroupBadge,
    SeniorityBadge,
    TrustedCircleBadge,
    LocationBadge,
} from './FilterBadges'
import GenerateWithAiModal from './GenerateWithAiModal'
import coreSignalApi from '../../../api/coreSignal/CoreSignalApi'
import helixSearchV2Api from '../../../api/helixSearchV2/HelixSearchV2Api'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import { MapExtra } from '../../../utils/MapExtra'

type Props = {
    filters: FilterInputs
    networkSeedPersons: PersonLiteDto[]
    onClose: () => void
    onSave: (nextFilters: FilterInputs) => void
    onOpenTrustedCircleModal?: () => void
    networkLinksPersonLiteCache?: MapExtra<string, PersonLiteDto>
    onSearchWithAi?: (
        nextFilters: FilterInputs,
        jobDescription?: string,
        experience?: ExperienceBounds,
        options?: SearchInvocationOptions,
    ) => void
    onSearch?: (
        nextFilters: FilterInputs,
        experience: ExperienceBounds,
        options?: SearchInvocationOptions,
    ) => void
    experienceMinYears: number
    experienceMaxYears: number
    defaultMinExperienceYears: number
    defaultMaxExperienceYears: number
    onExperienceMinYearsChange: (value: number) => void
    onExperienceMaxYearsChange: (value: number) => void
    hasRoleContext?: boolean
    savedJobDescription?: string | null
    onSaveFilters?: (
        nextFilters: FilterInputs,
        experience?: ExperienceBounds,
    ) => Promise<void>
    isSavingFilters?: boolean
    filtersModalError?: string | null
    onTrustedCircleChange?: (nextIds: string[]) => void
}

const keywordGroupFromTag = (value: string): KeywordGroup | null => {
    const keywords = value
        .split(/\s+or\s+/i)
        .map((keyword) => keyword.trim())
        .filter(Boolean)

    return keywords.length > 0 ? { keywords } : null
}

const keywordGroupToTag = (group: KeywordGroup): string | null => {
    const keywords = group.keywords
        .map((keyword) => keyword.trim())
        .filter(Boolean)

    return keywords.length > 0 ? keywords.join(' OR ') : null
}

const keywordGroupsFromTags = (tags: string[]): KeywordGroup[] =>
    tags
        .map(keywordGroupFromTag)
        .filter((group): group is KeywordGroup => group !== null)

const keywordGroupsToTags = (groups: KeywordGroup[]): string[] =>
    groups
        .map(keywordGroupToTag)
        .filter((group): group is string => group !== null)

const buildTrustedCircleDisplayLabel = (person: PersonLiteDto): string => {
    const name = formatPersonName(person) || 'Trusted Circle contact'
    const jobTitle = person.currentJobDetails?.workedAt?.jobTitle?.trim() ?? ''
    const companyName =
        person.currentJobDetails?.company?.name?.trim() ??
        person.currentJobDetails?.workedAt?.company?.name?.trim() ??
        ''

    const details = [jobTitle, companyName]
        .filter((value) => Boolean(value))
        .join(' @ ')

    return details ? `${name} - ${details}` : name
}

const buildTrustedCircleTagValue = (person: PersonLiteDto): string => {
    const label = buildTrustedCircleDisplayLabel(person)
    return label
}

const EXPERIENCE_TIMEFRAME_OPTIONS: Array<{
    value: ExperienceTimeframe
    label: string
}> = [
    { value: 'current_or_past', label: 'Current or past' },
    { value: 'current_only', label: 'Current only' },
    { value: 'past_only', label: 'Past only' },
]

export default function SearchFiltersModal({
    filters,
    networkSeedPersons,
    onClose,
    onSave,
    onOpenTrustedCircleModal,
    onSearchWithAi,
    onSearch,
    experienceMinYears,
    experienceMaxYears,
    defaultMinExperienceYears,
    defaultMaxExperienceYears,
    onExperienceMinYearsChange,
    onExperienceMaxYearsChange,
    hasRoleContext = false,
    savedJobDescription,
    onSaveFilters,
    isSavingFilters = false,
    filtersModalError,
    onTrustedCircleChange,
    networkLinksPersonLiteCache,
}: Props): React.JSX.Element {
    const [draftFilters, setDraftFilters] = useState<FilterInputs>(filters)
    const [isGenerateWithAiModalOpen, setIsGenerateWithAiModalOpen] =
        useState(false)
    const [isAutoPopulating, setIsAutoPopulating] = useState(false)
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false)
    const [titleExpansionSuggestions, setTitleExpansionSuggestions] = useState<
        string[]
    >([])
    const [titleExpansionLoading, setTitleExpansionLoading] = useState(false)
    const [titleExpansionError, setTitleExpansionError] = useState<
        string | null
    >(null)
    const titleExpansionRequestIdRef = useRef(0)

    useEffect(() => {
        setDraftFilters(filters)
    }, [filters])

    useEffect(() => {
        if (draftFilters.jobTitles.include.length === 0) {
            setTitleExpansionSuggestions([])
            setTitleExpansionError(null)
        }
    }, [draftFilters.jobTitles.include.length])

    const [draftExperienceMinYears, setDraftExperienceMinYears] =
        useState(experienceMinYears)
    const [draftExperienceMaxYears, setDraftExperienceMaxYears] =
        useState(experienceMaxYears)

    useEffect(() => {
        setDraftExperienceMinYears(experienceMinYears)
    }, [experienceMinYears])

    useEffect(() => {
        setDraftExperienceMaxYears(experienceMaxYears)
    }, [experienceMaxYears])

    const clampExperienceValue = (value: number): number => {
        const rounded = Math.round(value)
        return Math.min(
            defaultMaxExperienceYears,
            Math.max(defaultMinExperienceYears, rounded),
        )
    }

    const updateDraftMinYears = (value: number) => {
        const clamped = clampExperienceValue(value)
        setDraftExperienceMinYears(clamped)
        setDraftExperienceMaxYears((prev) => (prev < clamped ? clamped : prev))
    }

    const updateDraftMaxYears = (value: number) => {
        const clamped = clampExperienceValue(value)
        setDraftExperienceMaxYears(clamped)
        setDraftExperienceMinYears((prev) => (prev > clamped ? clamped : prev))
    }

    const applyExperienceChanges = () => {
        onExperienceMinYearsChange(draftExperienceMinYears)
        onExperienceMaxYearsChange(draftExperienceMaxYears)
    }

    const handleExperienceMinYearsChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        const value = Number(event.target.value)
        if (Number.isNaN(value)) {
            return
        }
        updateDraftMinYears(value)
    }

    const handleExperienceMaxYearsChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        const value = Number(event.target.value)
        if (Number.isNaN(value)) {
            return
        }
        updateDraftMaxYears(value)
    }

    const EXPERIENCE_MIN_YEARS = defaultMinExperienceYears
    const EXPERIENCE_MAX_YEARS = defaultMaxExperienceYears
    const experienceRange = Math.max(
        EXPERIENCE_MAX_YEARS - EXPERIENCE_MIN_YEARS,
        1,
    )
    const minPercent =
        ((draftExperienceMinYears - EXPERIENCE_MIN_YEARS) / experienceRange) *
        100
    const maxPercent =
        ((draftExperienceMaxYears - EXPERIENCE_MIN_YEARS) / experienceRange) *
        100
    const maxExperienceLabel =
        draftExperienceMaxYears === EXPERIENCE_MAX_YEARS
            ? `${EXPERIENCE_MAX_YEARS}+`
            : String(draftExperienceMaxYears)
    const experienceInputClassName =
        'mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-ctxt-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue focus-visible:ring-offset-1'

    const keywordGroupTags = useMemo(
        () => keywordGroupsToTags(draftFilters.keywordGroups),
        [draftFilters.keywordGroups],
    )

    const networkSeedPersonById = useMemo(() => {
        const map = new Map<string, PersonLiteDto>()
        for (const person of networkSeedPersons) {
            if (person.id) {
                map.set(person.id, person)
            }
        }
        if (networkLinksPersonLiteCache) {
            for (const id of networkLinksPersonLiteCache.getKeysAsArray()) {
                if (!map.has(id)) {
                    map.set(id, networkLinksPersonLiteCache.get(id)!)
                }
            }
        }
        return map
    }, [networkLinksPersonLiteCache, networkSeedPersons])

    const trustedCircleOptions = useMemo(() => {
        const seen = new Set<string>()
        const options: Array<{
            id: string
            tagValue: string
            displayLabel: string
        }> = []

        const addOption = (id: string) => {
            if (!id || seen.has(id)) {
                return
            }
            seen.add(id)
            const person = networkSeedPersonById.get(id)
            const displayLabel =
                (person && buildTrustedCircleDisplayLabel(person)) ?? id
            const tagValue =
                (person && buildTrustedCircleTagValue(person)) ?? displayLabel
            options.push({
                id,
                tagValue,
                displayLabel,
            })
        }

        for (const person of networkSeedPersons) {
            if (person.id) {
                addOption(person.id)
            }
        }

        if (networkLinksPersonLiteCache) {
            for (const id of networkLinksPersonLiteCache.getKeysAsArray()) {
                addOption(id)
            }
        }

        for (const id of draftFilters.trustedCirclePersonIds) {
            addOption(id)
        }

        return options
    }, [
        draftFilters.trustedCirclePersonIds,
        networkLinksPersonLiteCache,
        networkSeedPersons,
        networkSeedPersonById,
    ])

    const trustedCircleTagValueById = useMemo(() => {
        const map = new Map<string, string>()
        for (const option of trustedCircleOptions) {
            map.set(option.id, option.tagValue)
        }
        return map
    }, [trustedCircleOptions])

    const trustedCircleIdByTagValue = useMemo(() => {
        const map = new Map<string, string>()
        for (const option of trustedCircleOptions) {
            map.set(option.tagValue, option.id)
        }
        return map
    }, [trustedCircleOptions])

    const trustedCircleDisplayLabelById = useMemo(() => {
        const map = new Map<string, string>()
        for (const option of trustedCircleOptions) {
            map.set(option.id, option.displayLabel)
        }
        return map
    }, [trustedCircleOptions])

    const trustedCirclePersonByTagValue = useMemo(() => {
        const map = new Map<string, PersonLiteDto>()
        for (const option of trustedCircleOptions) {
            const person = networkSeedPersonById.get(option.id)
            if (person) {
                map.set(option.tagValue, person)
            }
        }
        return map
    }, [networkSeedPersonById, trustedCircleOptions])

    const getTrustedCircleOptionSearchText = useCallback(
        (option: string): string[] => {
            const searchTexts = [option]
            const person = trustedCirclePersonByTagValue.get(option)
            const department =
                person?.department?.trim() ||
                person?.currentJobDetails?.workedAt?.department?.trim()

            if (department) {
                searchTexts.push(department)
            }

            return searchTexts
        },
        [trustedCirclePersonByTagValue],
    )

    const trustedCircleTagValues = useMemo(
        () =>
            draftFilters.trustedCirclePersonIds.map(
                (id) => trustedCircleTagValueById.get(id) ?? id,
            ),
        [draftFilters.trustedCirclePersonIds, trustedCircleTagValueById],
    )

    const trustedCircleValidValues = useMemo(
        () => Array.from(trustedCircleIdByTagValue.keys()),
        [trustedCircleIdByTagValue],
    )

    const handleSaveOnly = async () => {
        applyExperienceChanges()
        if (isSavingFilters) {
            return
        }
        onSave(draftFilters)
        await onSaveFilters?.(draftFilters, buildExperienceBounds())
    }

    const buildExperienceBounds = (): ExperienceBounds => {
        const minYears = Math.min(
            draftExperienceMinYears,
            draftExperienceMaxYears,
        )
        const maxYears = Math.max(
            draftExperienceMinYears,
            draftExperienceMaxYears,
        )

        return {
            minYears,
            maxYears,
        }
    }

    const handleSearch = () => {
        applyExperienceChanges()
        onSave(draftFilters)
        onSearch?.(draftFilters, buildExperienceBounds(), {
            source: 'filters_modal',
        })
    }

    const handleSearchWithAi = () => {
        applyExperienceChanges()
        if (onSearchWithAi) {
            onSearchWithAi(draftFilters, undefined, buildExperienceBounds(), {
                source: 'filters_modal',
            })
        } else {
            onSave(draftFilters)
        }
    }

    const normalizeJobTitle = (value: string): string =>
        value.trim().toLowerCase()

    const filterNewTitleSuggestions = useCallback(
        (titles: string[], baseTitles: string[]): string[] => {
            const baseSet = new Set(
                baseTitles.map((title) => normalizeJobTitle(title)),
            )
            const seen = new Set<string>()
            const result: string[] = []

            for (const title of titles) {
                const trimmed = title.trim()
                if (!trimmed) {
                    continue
                }

                const normalized = normalizeJobTitle(trimmed)
                if (baseSet.has(normalized) || seen.has(normalized)) {
                    continue
                }

                seen.add(normalized)
                result.push(trimmed)

                if (result.length >= 20) {
                    break
                }
            }

            return result
        },
        [],
    )

    const addJobTitleToInclude = (title: string) => {
        const trimmed = title.trim()
        if (!trimmed) {
            return
        }

        setDraftFilters((prev) => {
            const normalized = normalizeJobTitle(trimmed)
            if (
                prev.jobTitles.include.some(
                    (existing) => normalizeJobTitle(existing) === normalized,
                )
            ) {
                return prev
            }

            return {
                ...prev,
                jobTitles: {
                    ...prev.jobTitles,
                    include: [...prev.jobTitles.include, trimmed],
                },
            }
        })
        removeTitleSuggestion(trimmed)
    }

    const removeTitleSuggestion = (title: string) => {
        const normalized = normalizeJobTitle(title)
        setTitleExpansionSuggestions((prev) =>
            prev.filter(
                (suggestion) => normalizeJobTitle(suggestion) !== normalized,
            ),
        )
    }

    const generateTitleExpansions = useCallback(
        async (sourceTitles: string[]) => {
            const baseTitles = sourceTitles
                .map((title) => title.trim())
                .filter((title) => title.length > 0)

            if (baseTitles.length === 0) {
                setTitleExpansionSuggestions([])
                setTitleExpansionError(
                    'Add at least one job title to generate AI expansions.',
                )
                return
            }

            const requestId = ++titleExpansionRequestIdRef.current
            setTitleExpansionLoading(true)
            setTitleExpansionError(null)

            try {
                const response = await coreSignalApi.createJobTitlesList({
                    jobTitlesFromProfile: baseTitles,
                })

                if (titleExpansionRequestIdRef.current !== requestId) {
                    return
                }

                const suggestions = filterNewTitleSuggestions(
                    response.jobTitles ?? [],
                    baseTitles,
                )

                setTitleExpansionSuggestions(suggestions)
                if (suggestions.length === 0) {
                    setTitleExpansionError(
                        'AI did not return any new title variations. Try refining the base titles or regenerating.',
                    )
                }
            } catch (error) {
                if (titleExpansionRequestIdRef.current !== requestId) {
                    return
                }
                console.error('Failed to expand job titles', error)
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Unable to expand job titles right now.'
                setTitleExpansionError(message)
                setTitleExpansionSuggestions([])
            } finally {
                if (titleExpansionRequestIdRef.current === requestId) {
                    setTitleExpansionLoading(false)
                }
            }
        },
        [filterNewTitleSuggestions],
    )

    const handleAddAllSuggestions = () => {
        titleExpansionSuggestions.forEach((suggestion) =>
            addJobTitleToInclude(suggestion),
        )
        setTitleExpansionSuggestions([])
    }

    const handleGenerateWithAi = async (jobDescription: string) => {
        applyExperienceChanges()
        const trimmedDescription = jobDescription.trim()
        if (trimmedDescription.length === 0) {
            return
        }

        setIsAutoPopulating(true)
        try {
            const response = await helixSearchV2Api.autoPopulateFilters({
                jobDescription: trimmedDescription,
            })

            setDraftFilters((prevDraftFilters) =>
                applyHelixSearchFilters(
                    prevDraftFilters,
                    response.filters ?? null,
                ),
            )
            toast.success('Filters populated with AI suggestions.')

            setIsGenerateWithAiModalOpen(false)
        } catch (error) {
            console.error('Failed to auto populate filters', error)
            toast.error(
                'AI filters could not be generated right now. Please try again.',
            )
        } finally {
            setIsAutoPopulating(false)
        }
    }

    const updateKeywordLogic = (value: KeywordLogic) => {
        setDraftFilters((prev) => ({ ...prev, keywordLogic: value }))
    }

    const updateJobTitlesTimeframe = (
        key: 'includeTimeframe' | 'excludeTimeframe',
        value: ExperienceTimeframe,
    ) => {
        setDraftFilters((prev) => ({
            ...prev,
            jobTitles: {
                ...prev.jobTitles,
                [key]: value,
            },
        }))
    }

    const updateCompaniesTimeframe = (
        key: 'includeTimeframe' | 'excludeTimeframe',
        value: ExperienceTimeframe,
    ) => {
        setDraftFilters((prev) => ({
            ...prev,
            companies: {
                ...prev.companies,
                [key]: value,
            },
        }))
    }

    const updateCompaniesList = (
        key: 'include' | 'exclude',
        values: string[],
    ) => {
        setDraftFilters((prev) => ({
            ...prev,
            companies: {
                ...prev.companies,
                [key]: values,
            },
        }))
    }

    const handleTrustedCircleIdsChange = useCallback(
        (nextIds: string[]) => {
            const uniqueNextIds = Array.from(new Set(nextIds))
            setDraftFilters((prev) => ({
                ...prev,
                trustedCirclePersonIds: uniqueNextIds,
                networkSeedPersonIds: uniqueNextIds,
            }))
            onTrustedCircleChange?.(uniqueNextIds)
        },
        [onTrustedCircleChange],
    )

    const handleClearTrustedCircle = useCallback(() => {
        handleTrustedCircleIdsChange([])
    }, [handleTrustedCircleIdsChange])

    const renderTimeframeRadios = (
        name: string,
        selected: ExperienceTimeframe,
        onChange: (value: ExperienceTimeframe) => void,
    ) => (
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-600">
            {EXPERIENCE_TIMEFRAME_OPTIONS.map((option) => (
                <label
                    key={option.value}
                    className="inline-flex items-center gap-2"
                >
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={selected === option.value}
                        onChange={(event) =>
                            onChange(event.target.value as ExperienceTimeframe)
                        }
                    />
                    {option.label}
                </label>
            ))}
        </div>
    )

    return (
        <>
            <Modal
                title="Edit Filters"
                onClose={onClose}
                size="medium"
                allowUserToClose={!isAutoPopulating}
                className="relative"
                rightFooter={
                    <>
                        <Button
                            type="button"
                            style="secondary"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            style="secondary"
                            onClick={handleSaveOnly}
                            enabled={!isSavingFilters}
                        >
                            Save Filters
                        </Button>
                        <Button
                            type="button"
                            style="primary"
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                        <Button
                            type="button"
                            style="primary"
                            classes="bg-yellow-300 text-amber-900 hover:bg-yellow-400 border border-yellow-300"
                            onClick={handleSearchWithAi}
                        >
                            <FontAwesomeIcon
                                icon={faWandMagicSparkles}
                                className="mr-2"
                            />
                            Search with AI
                        </Button>
                    </>
                }
            >
                {titleExpansionLoading && (
                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/80">
                        <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            className="text-ctxt-blue text-3xl"
                        />
                    </div>
                )}
                <div className="relative">
                    <div className="space-y-6">
                        {filtersModalError ? (
                            <Note
                                type={NoteType.ERROR}
                                containerClassNames="bg-white px-0"
                            >
                                {filtersModalError}
                            </Note>
                        ) : null}
                        <div className="flex">
                            <Button
                                type="button"
                                style="secondary"
                                onClick={() =>
                                    setIsGenerateWithAiModalOpen(true)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faWandMagicSparkles}
                                    className="mr-2"
                                />
                                Auto Populate
                            </Button>
                        </div>
                        <div>
                            <div className="flex items-center justify-between gap-4">
                                <div className="text-sm font-semibold text-gray-900">
                                    Trusted Circle
                                </div>
                                <div className="flex items-center gap-2">
                                    {draftFilters.trustedCirclePersonIds
                                        .length > 0 && (
                                        <Button
                                            type="button"
                                            style="secondary"
                                            size="small"
                                            onClick={handleClearTrustedCircle}
                                        >
                                            Clear all
                                        </Button>
                                    )}
                                    {onOpenTrustedCircleModal && (
                                        <Button
                                            type="button"
                                            style="secondary"
                                            size="small"
                                            onClick={onOpenTrustedCircleModal}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlusCircle}
                                                className="mr-2"
                                            />
                                            Add People
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="mt-3">
                                <TagsInput
                                    value={trustedCircleTagValues}
                                    onChange={(nextTags) => {
                                        const nextIds = nextTags.map(
                                            (tagValue) =>
                                                trustedCircleIdByTagValue.get(
                                                    tagValue,
                                                ) ?? tagValue,
                                        )
                                        handleTrustedCircleIdsChange(nextIds)
                                    }}
                                    placeholder={
                                        networkSeedPersons.length > 0
                                            ? 'Start typing to select a trusted contact'
                                            : 'No trusted circle contacts available'
                                    }
                                    validValues={trustedCircleValidValues}
                                    allowCustomValues={false}
                                    disabled={networkSeedPersons.length === 0}
                                    getOptionSearchText={
                                        getTrustedCircleOptionSearchText
                                    }
                                    renderOption={(option) => {
                                        const person =
                                            trustedCirclePersonByTagValue.get(
                                                option,
                                            )
                                        const personId =
                                            person?.id ||
                                            trustedCircleIdByTagValue.get(
                                                option,
                                            )
                                        const displayLabel =
                                            (personId &&
                                                trustedCircleDisplayLabelById.get(
                                                    personId,
                                                )) ||
                                            option
                                        const department =
                                            person?.department?.trim() ||
                                            person?.currentJobDetails?.workedAt?.department?.trim()

                                        return (
                                            <div className="flex w-full flex-col items-start">
                                                <span className="truncate">
                                                    {displayLabel}
                                                </span>
                                                {department ? (
                                                    <Badge
                                                        color={
                                                            BadgeColor.Violet
                                                        }
                                                        className="mt-1 rounded-md px-2 py-0.5 text-xs"
                                                        borderless={true}
                                                        title={department}
                                                    >
                                                        {department}
                                                    </Badge>
                                                ) : null}
                                            </div>
                                        )
                                    }}
                                    renderTag={(tag, { onRemove }) => {
                                        const personId =
                                            trustedCircleIdByTagValue.get(tag)
                                        const person = personId
                                            ? networkSeedPersonById.get(
                                                  personId,
                                              )
                                            : undefined
                                        const displayLabel =
                                            (personId &&
                                                trustedCircleDisplayLabelById.get(
                                                    personId,
                                                )) ||
                                            tag

                                        if (person) {
                                            return (
                                                <PersonBadge
                                                    person={person}
                                                    className="bg-gray-50"
                                                    rightIcon={
                                                        <button
                                                            type="button"
                                                            aria-label={`Remove ${displayLabel}`}
                                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold leading-none text-gray-700 hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue focus-visible:ring-offset-1"
                                                            onClick={(
                                                                event,
                                                            ) => {
                                                                event.stopPropagation()
                                                                onRemove()
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    }
                                                />
                                            )
                                        }

                                        return (
                                            <TrustedCircleBadge
                                                text={displayLabel}
                                                showRemove
                                                onRemove={onRemove}
                                            />
                                        )
                                    }}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-900">
                                        Years of experience
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        Defaults {defaultMinExperienceYears} –{' '}
                                        {defaultMaxExperienceYears} yrs
                                    </span>
                                </div>
                                <div className="mt-3 text-sm text-gray-900">
                                    Experience ({draftExperienceMinYears} -{' '}
                                    {maxExperienceLabel} years)
                                </div>
                                <div className="relative h-6">
                                    <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-gray-200" />
                                    <div
                                        className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-ctxt-blue"
                                        style={{
                                            left: `${minPercent}%`,
                                            right: `${100 - maxPercent}%`,
                                        }}
                                    />
                                    <input
                                        id="experience-range-min"
                                        type="range"
                                        min={EXPERIENCE_MIN_YEARS}
                                        max={EXPERIENCE_MAX_YEARS}
                                        step={1}
                                        value={draftExperienceMinYears}
                                        aria-label="Minimum experience in years"
                                        onChange={
                                            handleExperienceMinYearsChange
                                        }
                                        className="dual-range text-ctxt-blue"
                                    />
                                    <input
                                        id="experience-range-max"
                                        type="range"
                                        min={EXPERIENCE_MIN_YEARS}
                                        max={EXPERIENCE_MAX_YEARS}
                                        step={1}
                                        value={draftExperienceMaxYears}
                                        aria-label="Maximum experience in years"
                                        onChange={
                                            handleExperienceMaxYearsChange
                                        }
                                        className="dual-range text-ctxt-blue"
                                    />
                                </div>
                                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                                    <label className="text-xs font-semibold text-gray-600">
                                        Minimum
                                        <input
                                            type="number"
                                            min={defaultMinExperienceYears}
                                            max={defaultMaxExperienceYears}
                                            step={1}
                                            value={draftExperienceMinYears}
                                            onChange={
                                                handleExperienceMinYearsChange
                                            }
                                            className={experienceInputClassName}
                                        />
                                    </label>
                                    <label className="text-xs font-semibold text-gray-600">
                                        Maximum
                                        <input
                                            type="number"
                                            min={defaultMinExperienceYears}
                                            max={defaultMaxExperienceYears}
                                            step={1}
                                            value={draftExperienceMaxYears}
                                            onChange={
                                                handleExperienceMaxYearsChange
                                            }
                                            className={experienceInputClassName}
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Titles
                            </label>
                            <TagsInput
                                value={draftFilters.jobTitles.include}
                                onChange={(nextTags) =>
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        jobTitles: {
                                            ...prev.jobTitles,
                                            include: nextTags,
                                        },
                                    }))
                                }
                                placeholder="Type a title and press Enter or comma"
                                maxTagLength={80}
                                renderTag={(tag, { onRemove }) => (
                                    <IncludeJobTitleBadge
                                        text={tag}
                                        showRemove
                                        onRemove={onRemove}
                                    />
                                )}
                            />
                            {renderTimeframeRadios(
                                'job-titles-include-timeframe',
                                draftFilters.jobTitles.includeTimeframe,
                                (value) =>
                                    updateJobTitlesTimeframe(
                                        'includeTimeframe',
                                        value,
                                    ),
                            )}
                            <p className="mt-1 text-xs italic text-gray-500">
                                Combine titles freely: type literal values (e.g.
                                “software engineer”) and enclose any{' '}
                                <code>AND</code>/<code>OR</code> clauses in
                                parentheses, for example{' '}
                                <code>
                                    (engineer OR engineering) AND (manager OR
                                    head)
                                </code>
                                ; the backend expands those groups before
                                filtering.
                            </p>
                        </div>

                        <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                        AI title expansion
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Review these AI-generated title
                                        variations and tap to add them to your
                                        filters.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        style="secondary"
                                        size="small"
                                        enabled={
                                            !titleExpansionLoading &&
                                            titleExpansionSuggestions.length > 0
                                        }
                                        onClick={handleAddAllSuggestions}
                                    >
                                        Add all
                                    </Button>
                                    <Button
                                        type="button"
                                        style="secondary"
                                        size="small"
                                        enabled={
                                            !titleExpansionLoading &&
                                            draftFilters.jobTitles.include
                                                .length > 0
                                        }
                                        onClick={() =>
                                            void generateTitleExpansions(
                                                draftFilters.jobTitles.include,
                                            )
                                        }
                                    >
                                        {titleExpansionLoading
                                            ? 'Generating…'
                                            : 'Regenerate'}
                                    </Button>
                                </div>
                            </div>
                            {titleExpansionError && (
                                <div className="mt-3">
                                    <Note type={NoteType.ERROR}>
                                        {titleExpansionError}
                                    </Note>
                                </div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                                {titleExpansionSuggestions.map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        type="button"
                                        onClick={() =>
                                            addJobTitleToInclude(suggestion)
                                        }
                                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue focus-visible:ring-offset-1"
                                    >
                                        <span className="truncate">
                                            {suggestion}
                                        </span>
                                        <span className="text-[10px] text-gray-500">
                                            +
                                        </span>
                                    </button>
                                ))}
                                {!titleExpansionLoading &&
                                    titleExpansionSuggestions.length === 0 && (
                                        <p className="text-xs text-gray-500">
                                            {draftFilters.jobTitles.include
                                                .length === 0
                                                ? 'Add a title above to trigger AI expansions.'
                                                : 'No additional AI suggestions yet—add your own variants or tap regenerate.'}
                                        </p>
                                    )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Locations
                            </label>
                            <TagsInput
                                value={draftFilters.locations}
                                onChange={(nextTags) =>
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        locations: nextTags,
                                    }))
                                }
                                placeholder="e.g. London"
                                maxTagLength={80}
                                renderTag={(tag, { onRemove }) => (
                                    <LocationBadge
                                        text={tag}
                                        showRemove
                                        onRemove={onRemove}
                                    />
                                )}
                            />
                        </div>

                        <div className="rounded border border-gray-200 bg-white p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-900">
                                    Advanced filters
                                </span>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-ctxt-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue focus-visible:ring-offset-1"
                                    onClick={() =>
                                        setIsAdvancedFiltersOpen(
                                            (prev) => !prev,
                                        )
                                    }
                                    aria-expanded={isAdvancedFiltersOpen}
                                >
                                    {isAdvancedFiltersOpen ? 'Hide' : 'Show'}
                                    <FontAwesomeIcon
                                        icon={
                                            isAdvancedFiltersOpen
                                                ? faChevronUp
                                                : faChevronDown
                                        }
                                    />
                                </button>
                            </div>
                            {isAdvancedFiltersOpen && (
                                <div className="mt-4 space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                                            Titles to Exclude
                                        </label>
                                        <TagsInput
                                            value={
                                                draftFilters.jobTitles.exclude
                                            }
                                            onChange={(nextTags) =>
                                                setDraftFilters((prev) => ({
                                                    ...prev,
                                                    jobTitles: {
                                                        ...prev.jobTitles,
                                                        exclude: nextTags,
                                                    },
                                                }))
                                            }
                                            placeholder="Type a title and press Enter or comma"
                                            maxTagLength={80}
                                            renderTag={(tag, { onRemove }) => (
                                                <ExcludeJobTitleBadge
                                                    text={tag}
                                                    showRemove
                                                    onRemove={onRemove}
                                                />
                                            )}
                                        />
                                        {renderTimeframeRadios(
                                            'job-titles-exclude-timeframe',
                                            draftFilters.jobTitles
                                                .excludeTimeframe,
                                            (value) =>
                                                updateJobTitlesTimeframe(
                                                    'excludeTimeframe',
                                                    value,
                                                ),
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-semibold text-gray-900">
                                                Keyword Groups
                                            </label>
                                            <SegmentedToggleInput
                                                size="small"
                                                value={
                                                    draftFilters.keywordLogic
                                                }
                                                onChange={updateKeywordLogic}
                                                options={[
                                                    {
                                                        label: 'OR',
                                                        value: 'OR',
                                                        ariaLabel:
                                                            'Match any group (OR)',
                                                    },
                                                    {
                                                        label: 'AND',
                                                        value: 'AND',
                                                        ariaLabel:
                                                            'Match all groups (AND)',
                                                    },
                                                ]}
                                            />
                                        </div>
                                        <TagsInput
                                            value={keywordGroupTags}
                                            onChange={(nextTags) =>
                                                setDraftFilters((prev) => ({
                                                    ...prev,
                                                    keywordGroups:
                                                        keywordGroupsFromTags(
                                                            nextTags,
                                                        ),
                                                }))
                                            }
                                            placeholder="e.g. react OR nextjs"
                                            maxTagLength={120}
                                            renderTag={(tag, { onRemove }) => (
                                                <KeywordGroupBadge
                                                    text={tag}
                                                    showRemove
                                                    onRemove={onRemove}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                                            Companies
                                        </label>
                                        <TagsInput
                                            value={
                                                draftFilters.companies.include
                                            }
                                            onChange={(nextTags) =>
                                                updateCompaniesList(
                                                    'include',
                                                    nextTags,
                                                )
                                            }
                                            placeholder="Type a company name and press Enter or comma"
                                            maxTagLength={80}
                                            renderTag={(tag, { onRemove }) => (
                                                <IncludeCompanyBadge
                                                    text={tag}
                                                    showRemove
                                                    onRemove={onRemove}
                                                />
                                            )}
                                        />
                                        {renderTimeframeRadios(
                                            'companies-include-timeframe',
                                            draftFilters.companies
                                                .includeTimeframe,
                                            (value) =>
                                                updateCompaniesTimeframe(
                                                    'includeTimeframe',
                                                    value,
                                                ),
                                        )}
                                        <div className="mt-4">
                                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                                Companies to Exclude
                                            </label>
                                        </div>
                                        <TagsInput
                                            value={
                                                draftFilters.companies.exclude
                                            }
                                            onChange={(nextTags) =>
                                                updateCompaniesList(
                                                    'exclude',
                                                    nextTags,
                                                )
                                            }
                                            placeholder="Type a company name and press Enter or comma"
                                            maxTagLength={80}
                                            renderTag={(tag, { onRemove }) => (
                                                <ExcludeCompanyBadge
                                                    text={tag}
                                                    showRemove
                                                    onRemove={onRemove}
                                                />
                                            )}
                                        />
                                        {renderTimeframeRadios(
                                            'companies-exclude-timeframe',
                                            draftFilters.companies
                                                .excludeTimeframe,
                                            (value) =>
                                                updateCompaniesTimeframe(
                                                    'excludeTimeframe',
                                                    value,
                                                ),
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <div className="text-sm font-semibold text-gray-900 mb-2">
                                                Seniorities
                                            </div>
                                            <TagsInput
                                                value={draftFilters.seniorities}
                                                onChange={(nextTags) =>
                                                    setDraftFilters((prev) => ({
                                                        ...prev,
                                                        seniorities:
                                                            nextTags as Seniority[],
                                                    }))
                                                }
                                                placeholder="Start typing to select a seniority"
                                                validValues={SENIORITY_VALUES}
                                                renderTag={(
                                                    tag,
                                                    { onRemove },
                                                ) => (
                                                    <SeniorityBadge
                                                        text={tag}
                                                        showRemove
                                                        onRemove={onRemove}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900 mb-2">
                                            Departments
                                        </div>
                                        <TagsInput
                                            value={draftFilters.jobFunctions}
                                            onChange={(nextTags) =>
                                                setDraftFilters((prev) => ({
                                                    ...prev,
                                                    jobFunctions:
                                                        nextTags as JobFunction[],
                                                }))
                                            }
                                            placeholder="Start typing to select a job function"
                                            validValues={JOB_FUNCTION_VALUES}
                                            renderTag={(tag, { onRemove }) => (
                                                <JobFunctionBadge
                                                    text={tag}
                                                    showRemove
                                                    onRemove={onRemove}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
            {isGenerateWithAiModalOpen && (
                <GenerateWithAiModal
                    onClose={() => setIsGenerateWithAiModalOpen(false)}
                    onGenerate={handleGenerateWithAi}
                    savedJobDescription={savedJobDescription}
                    hasRoleContext={hasRoleContext}
                    isGenerating={isAutoPopulating}
                />
            )}
        </>
    )
}
